import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const sessionId = `nova-${Math.random().toString(36).slice(2)}-${Date.now()}`;

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Namaste! I'm Nova, your Nova Royale guide. Ask me about plots, amenities, location or pricing. 🌿" },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect. Please call +91 73491 39671." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2.4, type: "spring", stiffness: 200 }}
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full text-[#1C2427] shadow-xl transition-transform hover:scale-110"
        style={{ background: "var(--gold)" }}
        data-testid="chatbot-toggle"
        aria-label="Chat with Nova"
      >
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[60] flex h-[70vh] max-h-[520px] w-[90vw] max-w-[380px] flex-col overflow-hidden rounded-lg border border-[var(--cream-3)] bg-[var(--cream)] shadow-2xl"
            data-testid="chatbot-panel"
          >
            <div className="flex items-center gap-3 px-5 py-4" style={{ background: "var(--ink)" }}>
              <div className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: "var(--gold)" }}>
                <span className="font-serif-display text-lg text-[#1C2427]">N</span>
              </div>
              <div>
                <p className="text-sm font-500 text-[var(--cream)]">Nova · Sales Assistant</p>
                <p className="text-[10px] uppercase tracking-widest text-[var(--gold-bright)]">Nova Royale</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[var(--ink)] text-[var(--cream)]"
                        : "bg-[var(--cream-2)] text-[var(--ink)]"
                    }`}
                  >
                    {m.content || <span className="inline-block animate-pulse">…</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 border-t border-[var(--cream-3)] p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about Nova Royale…"
                className="flex-1 rounded-sm bg-[var(--cream-2)] px-3 py-2.5 text-sm outline-none placeholder:text-[var(--ink-soft)]/60 focus:ring-1 focus:ring-[var(--gold)]"
                data-testid="chatbot-input"
              />
              <button
                onClick={send}
                disabled={busy}
                className="flex h-10 w-10 items-center justify-center rounded-sm text-[#1C2427] transition-opacity disabled:opacity-50"
                style={{ background: "var(--gold)" }}
                data-testid="chatbot-send"
              >
                <Send size={17} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
